import React from 'react';
import { Platform } from 'react-native';
import { Column } from '@tanstack/react-table';

export const useColumnResizing = (
  tableColumns: Record<string, { width?: number }>,
  minColumnWidth: number = 60,
  maxColumnWidth: number = 640,
) => {
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>({});

  const setRowWidths = React.useCallback((initial: Record<string, number>) => {
    setColumnWidths((prev) => ({ ...initial, ...prev }));
  }, []);

  React.useEffect(() => {
    const initial: Record<string, number> = {};
    Object.keys(tableColumns || {}).forEach((key) => {
      const w = tableColumns[key]?.width;
      if (typeof w === 'number') initial[key] = w;
    });
    setRowWidths(initial);
  }, [tableColumns, setRowWidths]);

  const handleResize = (id: string, startX: number, currentWidth: number, col: Column<unknown, unknown>) => {
    if (Platform.OS !== 'web') return;

    const onMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      const next = Math.min(Math.max(currentWidth + delta, minColumnWidth), maxColumnWidth);
      setColumnWidths((prev) => ({ ...prev, [id]: next }));
      (col as { setSize?: (size: number) => void }).setSize?.(next);
    };

    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true });
  };

  return {
    columnWidths,
    setColumnWidths,
    handleResize,
  };
};
